import { StorefrontRounded } from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  SvgIcon,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Search as SearchIcon, XCircle as XIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NativeScrollbar from "src/components/NativeScrollbar";
import TextInputSearch from "src/components/TextInputSearch";
import arkadu from "src/coreConfig";
import { useSelector } from "src/store";
import axios from "src/utils/axios";

const Search = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchType, setSearchType] = useState("users");
  const [lastQuery, setLastQuery] = useState("");
  const { currentCustomer } = useSelector((state) => state.customers);
  const { adminMode } = useSelector((state) => state.settings);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const runSearch = async (value, type) => {
    if (!value) return;
    try {
      setLoading(true);
      const url =
        type === "licenses"
          ? arkadu.urls.v2.taxes.license.search
          : arkadu.urls.v2.user.search;

      const response = await axios.post(url, {
        customer_id: currentCustomer.id,
        searchText: value,
        mode: adminMode ? "admin" : "user",
      });

      if (response.data.res === 1) {
        setResults(response.data.data || []);
      } else {
        setResults([]);
        enqueueSnackbar(response.data.message);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Something went wrong", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (v) => {
    setLastQuery(v);
    await runSearch(v, searchType);
  };

  const handleTypeChange = async (event, value) => {
    setSearchType(value);
    setResults([]);
    if (lastQuery) {
      await runSearch(lastQuery, value);
    }
  };

  if (!currentCustomer) {
    return null;
  }

  return (
    <>
      <Tooltip title="Search">
        <IconButton color="inherit" onClick={handleOpen} size="large">
          <SvgIcon fontSize="small">
            <SearchIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        ModalProps={{ BackdropProps: { invisible: true } }}
        onClose={handleClose}
        open={isOpen}
        variant="temporary"
        slotProps={{
          paper: {
            sx: {
              width: 500,
              maxWidth: "100%",
              backgroundColor: theme.palette.background.paper,
            },
          },
        }}
      >
        <NativeScrollbar options={{ suppressScrollX: true }}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h4" color="text.primary">
                {t("Buscar")}
              </Typography>
              <IconButton onClick={handleClose} size="large">
                <SvgIcon fontSize="small">
                  <XIcon />
                </SvgIcon>
              </IconButton>
            </Box>
            <Tabs
              value={searchType}
              onChange={handleTypeChange}
              sx={{ mt: 1 }}
              variant="fullWidth"
            >
              <Tab value="users" label={t("Usuarios")} />
              <Tab value="licenses" label={t("Establecimientos")} />
            </Tabs>
            <Box sx={{ mt: 2 }}>
              <TextInputSearch onSearch={handleSearch} min={3} />
            </Box>

            <Box sx={{ mt: 4 }}>
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : (
                <MenuList>
                  {results.map((result, i) => (
                    <MenuItem
                      key={i}
                      onClick={() => {
                        navigate(
                          searchType === "licenses"
                            ? `/${currentCustomer.shortname}/taxes/license/${result.id}/summary`
                            : `/${currentCustomer.shortname}/admin/user/${result.id}`,
                        );

                        handleClose();
                      }}
                    >
                      <ListItemIcon>
                        {searchType === "licenses" ? (
                          <StorefrontRounded />
                        ) : (
                          <Avatar
                            alt={result.fullname}
                            src={result.avatar}
                            sx={{ height: 32, width: 32 }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          searchType === "licenses"
                            ? result.alias || result.number
                            : result.fullname
                        }
                        secondary={
                          searchType === "licenses"
                            ? `${result.number || ""} ${result.address || ""}`
                            : result.email
                        }
                      />
                    </MenuItem>
                  ))}
                </MenuList>
              )}
            </Box>
          </Box>
        </NativeScrollbar>
      </Drawer>
    </>
  );
};

export default Search;
